import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

export interface SocietyFeatures {
  noticeboardEnabled: boolean;
  eventsEnabled: boolean;
}

interface SocietyFeaturesContextValue {
  features: SocietyFeatures;
  setFeature: (key: keyof SocietyFeatures, value: boolean) => void;
}

const DEFAULT_FEATURES: SocietyFeatures = {
  noticeboardEnabled: true,
  eventsEnabled: true,
};

const SocietyFeaturesContext = createContext<SocietyFeaturesContextValue>({
  features: DEFAULT_FEATURES,
  setFeature: () => {},
});

export const useSocietyFeatures = () => useContext(SocietyFeaturesContext);

const storageKey = (societyId: string) => `nexora_features_${societyId}`;

export const SocietyFeaturesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [features, setFeatures] = useState<SocietyFeatures>(DEFAULT_FEATURES);

  useEffect(() => {
    if (!user?.societyId) {
      setFeatures(DEFAULT_FEATURES);
      return;
    }
    const stored = localStorage.getItem(storageKey(user.societyId));
    if (stored) {
      try {
        setFeatures({ ...DEFAULT_FEATURES, ...JSON.parse(stored) });
      } catch {
        setFeatures(DEFAULT_FEATURES);
      }
    } else {
      setFeatures(DEFAULT_FEATURES);
    }
  }, [user?.societyId]);

  const setFeature = useCallback(
    (key: keyof SocietyFeatures, value: boolean) => {
      setFeatures(prev => {
        const next = { ...prev, [key]: value };
        if (user?.societyId) {
          localStorage.setItem(storageKey(user.societyId), JSON.stringify(next));
        }
        return next;
      });
    },
    [user?.societyId],
  );

  return (
    <SocietyFeaturesContext.Provider value={{ features, setFeature }}>
      {children}
    </SocietyFeaturesContext.Provider>
  );
};
